import React, { useMemo, useState } from 'react';
import { Col, Label, Row, Table } from 'reactstrap';
import { CustomerActivityReportDto } from '../../requests/useGetCustomerActivityReport';
import { dateSort, dateSortReverse } from '../../Utilities';
import { toAbbreviation } from '../Common/Units';
import { SortableColumnHeader, SortDirection } from '../Common/SortableColumnHeader';
import { isMultiValue, KaSelect, KaSelectOption } from '../Common/KaSelect';

export const reportColumns = [
  'Site',
  'Customer',
  'Destination',
  'Branch',
  'Driver',
  'Transport',
  'Carrier',
  'Applicator',
  'Notes',
  'Products'
]

type SortMode =
  { isHighlighted: boolean, field: SortField, direction: SortDirection }

enum SortField {
  loadedAt,
  orderNumber
}

const defaultSort: SortMode = ({
  isHighlighted: false,
  field: SortField.loadedAt,
  direction: SortDirection.ascending
});

interface CustomerActivityReportDisplayProps {
  reportData: CustomerActivityReportDto[];
  downloadQuery: () => Promise<void>;
  columns: string[];
}

export const CustomerActivityReportDisplay: React.FC<CustomerActivityReportDisplayProps> = ({ reportData, downloadQuery, columns }) => {
  const [sortMode, setSortMode] = useState(defaultSort);
  const [generatedAt] = useState(new Date().toLocaleString());
  const [groupedField, setGroupedField] = useState<string>("");
  const [showTotals, setShowTotals] = useState<boolean>(true);

  const productColumns = useMemo(() => {
    const productMap = new Map<string, string>();

    for (const transaction of reportData) {
      for (const [product, measurement] of Object.entries(transaction.productMeasurements)) {
        if (!productMap.has(product) && measurement.unit) {
          productMap.set(product, measurement.unit);
        }
      }
    }

    return Array.from(productMap.entries())
      .map(([name, unit]) => ({ name, unit }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [reportData]);

  const columnDisplayState = reportColumns.map(rc => ({ name: rc, show: columns.includes(rc) }))
  const isColumnSelected = (name: string) => columnDisplayState.find(c => c.name === name)!.show;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleSortMode = (sortField: SortField) => {
    setSortMode((prev) => {
      const isSameFieldSelected = prev.isHighlighted && prev.field === sortField;

      return {
        isHighlighted: true,
        field: sortField,
        direction: isSameFieldSelected && prev.direction === SortDirection.ascending
          ? SortDirection.descending
          : SortDirection.ascending
      };
    });
  };

  const transactionToDataRow = (transaction: CustomerActivityReportDto, background: string = 'bg-white') =>
    <tr key={transaction.ticketNumber} className={`border-top ${background}`}>
      <td className="py-1 text-nowrap">{formatDate(transaction.loadedAt)}</td>
      <td className="py-1 text-nowrap">{transaction.orderNumber || '-'}</td>
      <td className="py-1 text-nowrap">{transaction.ticketNumber}</td>
      {isColumnSelected("Customer") && <td className="py-1 text-nowrap">{transaction.customerName || '-'}</td>}
      {isColumnSelected("Destination") && <td className="py-1 text-nowrap">{transaction.destination || '-'}</td>}
      {isColumnSelected("Branch") && <td className="py-1 text-nowrap">{transaction.branch || '-'}</td>}
      {isColumnSelected("Site") && <td className="py-1 text-nowrap">{transaction.site}</td>}
      {isColumnSelected("Driver") && <td className="py-1 text-nowrap">{transaction.driver || '-'}</td>}
      {isColumnSelected("Transport") && <td className="py-1 text-nowrap">{transaction.transport || '-'}</td>}
      {isColumnSelected("Carrier") && <td className="py-1 text-nowrap">{transaction.carrier || '-'}</td>}
      {isColumnSelected("Applicator") && <td className="py-1 text-nowrap">{transaction.applicator || '-'}</td>}
      {isColumnSelected("Notes") && <td className="py-1">{transaction.notes || '-'}</td>}
      {isColumnSelected("Products") && productColumns.map(product => (
        <td key={product.name} className="text-end py-1 text-nowrap">
          {
            transaction.productMeasurements[product.name].error
              ? 'N/A'
              : transaction.productMeasurements[product.name].quantity?.toFixed(2) || '0'
          }
        </td>
      ))}
    </tr>

  const isSortColumnHighlighted = (sortField: SortField) =>
    sortMode.isHighlighted && sortField === sortMode.field;

  const allSortedData = useMemo(() => {
    if (sortMode.field === SortField.loadedAt) {
      return sortMode.direction === SortDirection.ascending
        ? dateSort(reportData, 'loadedAt')
        : dateSortReverse(reportData, 'loadedAt');
    }

    return [...reportData].sort((a, b) =>
      sortMode.direction === SortDirection.ascending
        ? a.orderNumber!.localeCompare(b.orderNumber!)
        : b.orderNumber!.localeCompare(a.orderNumber!)
    );
  }, [reportData, sortMode]);

  const groupingOptions = ["Site", "Transport", "Order"].map<KaSelectOption>(s => ({ value: s, label: s }));

  const convertToDtoField = (groupedField: string) => {
    return (() => {
      switch (groupedField) {
        case "Order": return "orderNumber" as keyof CustomerActivityReportDto;
        default: return groupedField.toLowerCase() as keyof CustomerActivityReportDto;
      }
    })()
  }

  const groups = useMemo(() => {
    if (!groupedField) return null;

    const field = convertToDtoField(groupedField);
    const map = new Map<string, CustomerActivityReportDto[]>();

    for (const dto of allSortedData) {
      const key = dto[field];
      if (!key) continue;

      let group = map.get(key as string);
      if (!group) {
        group = [];
        map.set(key as string, group);
      }
      group.push(dto);
    }

    return map;
  }, [allSortedData, groupedField]);
  
  const groupTotals = useMemo(() => {
    if (!groups || !showTotals) return null;

    const totals = new Map<string, Record<string, number>>();

    for (const [groupKey, rows] of groups) {
      const productTotals: Record<string, number> = {};

      for (const row of rows) {
        for (const product of productColumns) {
          productTotals[product.name] =
            (productTotals[product.name] ?? 0) +
            (row.productMeasurements[product.name]?.quantity ?? 0);
        }
      }

      totals.set(groupKey, productTotals);
    }

    return totals;
  }, [groups, productColumns, showTotals]);

  const grandTotals = useMemo(() => {
    const totals: Record<string, number> = {};

    for (const transaction of reportData) {
      for (const product of productColumns) {
        totals[product.name] =
          (totals[product.name] ?? 0) +
          (transaction.productMeasurements[product.name]?.quantity ?? 0);
      }
    }

    return totals;
  }, [reportData, productColumns]);

  return (
    <div className="container-fluid p-4">
      <Row>
        <Col className="col-auto pe-0">
          <h1 className="ka-blue">Customer Activity Report</h1>
        </Col>
        <Col className="px-0">
          <button className="btn py-0 px-3" onClick={() => downloadQuery()} data-testid="download">
            <i className="bi bi-cloud-download ka-blue" style={{ fontSize: "2em" }} />
          </button>
        </Col>
      </Row>
      <p className="text-muted mb-5">Generated on {generatedAt}</p>
      <Row className="mb-2">
        <Col className="col-2" style={{ zIndex: 1021 }}>
          <Label htmlFor="grouping-selector" className="fw-bold form-label">
            Group Transactions
          </Label>
          <KaSelect
            inputId="grouping-selector"
            placeholder="Group by..."
            options={groupingOptions}
            value={groupingOptions.find(go => go.value === groupedField)}
            onChange={(option) => setGroupedField(isMultiValue(option) ? "" : (option?.value || ""))}
            isClearable={true}
          />
        </Col>
      </Row>
      <Row>
        <Col className="form-check ms-3">
          <input
            type="checkbox"
            id="toggleShowTotals"
            className="form-check-input"
            checked={showTotals}
            onChange={() => setShowTotals(!showTotals)} />
          <label htmlFor="toggleShowTotals" className="form-check-label">
            Show Product Totals
          </label>
        </Col>
      </Row>
      {reportData.length > 0 ? (
        <div className="table">
          <Table borderless>
            <thead className="sticky-top bg-white">
              <tr className="border-bottom border-dark border-1">
                <th>
                  <SortableColumnHeader
                    name="Date/Time"
                    isActive={isSortColumnHighlighted(SortField.loadedAt)}
                    direction={sortMode.direction}
                    handleSort={() => handleSortMode(SortField.loadedAt)}
                  />
                </th>
                <th>
                  <SortableColumnHeader
                    name="Order #"
                    isActive={isSortColumnHighlighted(SortField.orderNumber)}
                    direction={sortMode.direction}
                    handleSort={() => handleSortMode(SortField.orderNumber)}
                  />
                </th>
                <th>Ticket #</th>
                {isColumnSelected("Customer") && <th>Customer</th>}
                {isColumnSelected("Destination") && <th>Destination</th>}
                {isColumnSelected("Branch") && <th>Branch</th>}
                {isColumnSelected("Site") && <th>Site</th>}
                {isColumnSelected("Driver") && <th>Driver</th>}
                {isColumnSelected("Transport") && <th>Transport</th>}
                {isColumnSelected("Carrier") && <th>Carrier</th>}
                {isColumnSelected("Applicator") && <th>Applicator</th>}
                {isColumnSelected("Notes") && <th>Notes</th>}
                {isColumnSelected("Products") && productColumns.map(product => (
                  <th key={product.name} className="text-end">
                    {product.name}
                    {product.unit && (
                      <div className="text-muted small">({toAbbreviation(product.unit)})</div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups === null && allSortedData.map(transaction => transactionToDataRow(transaction))}
              {groups !== null && (Array.from(groups.entries())).map((group, index) => <>
                <tr key={group[0]} className={`border-top ${index % 2 === 1 ? "bg-row-grouping" : "bg-white"}`}>
                  <td className="py-1 text-nowrap fw-bold fs-4">{group[0]}</td>
                  <td className="py-1 text-nowrap"></td>
                  <td className="py-1 text-nowrap"></td>
                  {isColumnSelected("Customer") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Destination") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Branch") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Site") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Driver") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Transport") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Carrier") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Applicator") && <td className="py-1 text-nowrap"></td>}
                  {isColumnSelected("Notes") && <td className="py-1"></td>}
                  {isColumnSelected("Products") && productColumns.map(product => (
                    <td key={product.name} className="text-end py-1 text-nowrap fw-bold fs-4">
                      {showTotals && groupTotals?.get(group[0])?.[product.name]?.toFixed(2)}
                    </td>
                  ))}
                </tr>
                {group[1].map(transaction => transactionToDataRow(transaction, index % 2 === 1 ? "bg-row-grouping" : "bg-white"))}
              </>
              )}
              {showTotals && <tr key="totalRow" className={`border-top ${groups === null || groups.size % 2 === 1 ? "bg-row-grouping" : "bg-white"}`}>
                <td className="py-1 text-nowrap fw-bold fs-3">Totals</td>
                <td className="py-1 text-nowrap"></td>
                <td className="py-1 text-nowrap"></td>
                {isColumnSelected("Customer") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Destination") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Branch") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Site") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Driver") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Transport") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Carrier") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Applicator") && <td className="py-1 text-nowrap"></td>}
                {isColumnSelected("Notes") && <td className="py-1"></td>}
                {isColumnSelected("Products") && productColumns.map(product => (
                  <td key={product.name} className="text-end py-1 text-nowrap fw-bold fs-3">
                    {showTotals && (grandTotals[product.name] ?? 0).toFixed(2)}
                  </td>
                ))}
              </tr>}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox fs-1 d-block mb-3"></i>
          <h4>No Data Available</h4>
          <p>No order transactions found for this business.</p>
        </div>
      )}
    </div>
  );
};